import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Menu } from 'projects/app-core/src/app/models/menu';
import { IAnyoUser } from 'projects/app-core/src/auth/models/IAnyoUser';
import { Permissions, Roles } from 'projects/app-core/src/auth/models/roles';
import { AuthService } from '../../../../../app-core/src/auth/auth.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
})
export class MainLayoutComponent implements OnInit {
  lastScrollTop = 0;
  isHeaderHidden = false;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const currentScroll =
      window.pageYOffset || document.documentElement.scrollTop;

    if (currentScroll > this.lastScrollTop && currentScroll > 100) {
      // Scrolling down
      this.isHeaderHidden = true;
    } else {
      // Scrolling up
      this.isHeaderHidden = false;
    }

    this.lastScrollTop = currentScroll <= 0 ? 0 : currentScroll; // For mobile scroll bounce
  }

  async logout() {
    await this.auth.signOut();
  }

  menu: Menu[] = [
    {
      name: 'Dashboard',
      icon: 'assets/icons/dashboard.svg',
      nzIcon: 'appstore',
    },
  ];

  user: string = '';
  domainNameMessage: string = environment.domain;
  constructor(
    private auth: AuthService,
    private router: Router,
  ) {}
  /**
   * Helper method to check if the user has SuperAdmin role
   */
  private isSuperAdmin(customClaims: IAnyoUser['customClaims']): boolean {
    return customClaims?.roles?.includes(Roles.SuperAdmin) || false;
  }

  checkAndMarkIsSelected() {
    const currentUrl = this.router.url;
    this.menu.forEach((menuItem) => {
      if (menuItem.subMenu) {
        menuItem.subMenu.forEach((subItem) => {
          subItem.isSelected = false;

          if (currentUrl.startsWith(subItem.link)) {
            subItem.isSelected = true;
          }

          if (subItem.subMenu && subItem.subMenu.length) {
            subItem.subMenu.forEach((data) => {
              data.isSelected = false;

              if (currentUrl.startsWith(data.link)) {
                data.isSelected = true;
                subItem.isSelected = true;
              }
            });
          }
        });
      }
    });

    if (currentUrl === '/') {
      this.menu[0].isSelected = true;
    }
  }
  async ngOnInit(): Promise<void> {
    this.auth.currentAuthStatus.subscribe((user) => {
      if (user) {
        this.user = user.name!;
        // Check if user is SuperAdmin first
        if (this.isSuperAdmin(user.customClaims)) {
          this.handleSuperAdminMenus();
        } else {
          this.handleAdditionalPermissions(user.customClaims);
          this.handleRoles(user.customClaims);
        }
        this.checkAndMarkIsSelected();
      }
    });
  }

  /**
   * Handle menu creation for SuperAdmin users - automatically shows all available menus
   * This method creates a mock user with all permissions and roles to trigger all menu creation logic
   */
  private handleSuperAdminMenus(): void {
    // Create a mock user with all possible permissions and roles
    const mockSuperAdminClaims: IAnyoUser['customClaims'] = {
      roles: Object.values(Roles),
      additionalPermissions: Object.values(Permissions),
    };

    // Call both permission handlers with the mock claims
    // This will automatically create all menus without manual maintenance
    this.handleAdditionalPermissions(mockSuperAdminClaims);
    this.handleRoles(mockSuperAdminClaims);
  }

  handleAdditionalPermissions(customClaims: IAnyoUser['customClaims']) {
    if (!customClaims) return;

    if (
      [
        Permissions.AdminManageTherapist,
        Permissions.AdminManageListener,
        Permissions.AddTherapist,
      ].some((per) => customClaims.additionalPermissions.includes(per))
    ) {
      this.menu.push({
        name: 'Experts',
        icon: 'assets/icons/listener.svg',
        nzIcon: 'usergroup-add',
        subMenu: [
          {
            name: 'Manage Experts',
            link: '/experts',
          },
          {
            name: 'Mange Onboarding Requests',
            link: '/experts/onboarding',
          },
        ],
      });
    }

    if (
      [
        Permissions.AdminManageTherapist,
        Permissions.AdminManageAppointment,
        Permissions.AdminManageTherapyPlans,
      ].some((permission) =>
        customClaims.additionalPermissions.includes(permission),
      ) ||
      customClaims.roles.includes(Roles.ManageAnyoPackages)
    ) {
      this.menu.push({
        name: 'Therapists',
        icon: 'assets/icons/therapist.svg',
        nzIcon: 'user-switch',
        subMenu: [],
      });
    }

    if (
      customClaims.additionalPermissions.includes(
        Permissions.AdminManageTherapist,
      )
    ) {
      const index = this.menu.findIndex((data) => data.name === 'Therapists');
      this.menu[index].subMenu?.push({
        name: 'Manage Therapist',
        link: '/therapist/manage',
      });
      this.menu[index].subMenu?.push({
        name: 'Calender View',
        link: '/calender/home',
      });
    }

    if (customClaims.roles.includes(Roles.ManageUserAttributes)) {
      const index = this.menu.findIndex((data) => data.name === 'Therapists');
      this.menu[index].subMenu?.push({
        name: 'Manage User Attributes',
        link: '/therapist/manage-user-attributes',
      });
    }

    if (
      customClaims.additionalPermissions.includes(
        Permissions.AdminManageAppointment,
      )
    ) {
      const index = this.menu.findIndex((data) => data.name === 'Therapists');
      this.menu[index].subMenu?.push({
        name: 'Manage Appointments',
        link: '/therapist/session/admin',
      });
      this.menu[index].subMenu!.push({
        name: 'Therapy Plan Subscriptions',
        link: '/therapy-plans/all-users-subscriptions',
      });
    }
    if (
      customClaims.additionalPermissions.includes(
        Permissions.AdminManageAppointment,
      )
    ) {
      const index = this.menu.findIndex((data) => data.name === 'Therapists');
      this.menu[index].subMenu?.push({
        name: 'Manage Ratings',
        link: '/therapist/ratings',
      });
    }
    if (
      customClaims.additionalPermissions.includes(
        Permissions.AdminManageAppointment,
      )
    ) {
      const index = this.menu.findIndex((data) => data.name === 'Therapists');
      this.menu[index].subMenu?.push({
        name: 'Therapy Plans',
        link: '/therapy-plans/list',
        subMenu: [],
      });
      const subIndex = this.menu[index].subMenu!.findIndex(
        (data) => data.name === 'Therapy Plans',
      );
      this.menu[index].subMenu![subIndex].subMenu?.push({
        name: 'Manage Therapy Plans',
        link: '/therapy-plans/list',
      });
    }
    if (
      customClaims.additionalPermissions.includes(
        Permissions.AdminManageAppointment,
      )
    ) {
      const index = this.menu.findIndex((data) => data.name === 'Therapists');
      const subIndex = this.menu[index].subMenu!.findIndex(
        (data) => data.name === 'Therapy Plans',
      );
      this.menu[index].subMenu![subIndex].subMenu?.push({
        name: 'All Therapy Plans',
        link: '/therapy-plans/all',
      });
    }

    if (
      customClaims.additionalPermissions.includes(
        Permissions.AdminManageAppointment,
      )
    ) {
      const index = this.menu.findIndex((data) => data.name === 'Therapists');
      const subIndex = this.menu[index].subMenu!.findIndex(
        (data) => data.name === 'Therapy Plans',
      );
      this.menu[index].subMenu![subIndex].subMenu?.push({
        name: 'Therapy Plan Subscriptions',
        link: '/therapy-plans/all-users-subscriptions',
      });
    }

    if (customClaims.roles.includes(Roles.ManageAnyoPackages)) {
      const index = this.menu.findIndex((data) => data.name === 'Therapists');
      this.menu[index].subMenu?.push({
        name: 'Therapy Packages',
        link: '/therapy-packages/list',
        subMenu: [
          {
            name: 'Manage Anyo packs',
            link: '/packs',
          },
          {
            name: 'Manage Anyo packs subscription',
            link: '/packs/packs-subscription',
          },
          {
            name: 'Chat With Prompt',
            link: '/packs/chat',
          },
        ],
      });
    }

    // if (
    //   customClaims.additionalPermissions.includes(
    //     Permissions.AdminManageListener,
    //   )
    // ) {
    //   this.menu.push({
    //     name: 'Listener',
    //     icon: 'assets/icons/listener.svg',
    //     nzIcon: 'user',
    //     subMenu: [
    //       {
    //         name: 'Manage Listener',
    //         link: '/listener',
    //       },
    //     ],
    //   });
    // }
    const ledgerSubMenu: NonNullable<Menu['subMenu']> = [];
    if (
      customClaims.additionalPermissions.includes(
        Permissions.AdminManageExpertsRewards,
      )
    ) {
      ledgerSubMenu.push(
        {
          name: 'Manage Reward Values',
          link: '/ledger/manage-rewards',
        },
        {
          name: 'Manage Expert Rewards',
          link: '/ledger/manage-experts-rewards',
        },
      );
    }
    if (
      customClaims.additionalPermissions.includes(
        Permissions.AdminManageTherapist,
      )
    ) {
      ledgerSubMenu.push({
        name: 'Manage Session Rewards',
        link: '/ledger/session-rewards',
      });
    }
    if (ledgerSubMenu.length > 0) {
      this.menu.push({
        name: 'Ledger',
        icon: 'assets/icons/listener.svg',
        nzIcon: 'gift',
        subMenu: ledgerSubMenu,
      });
    }
    if (
      customClaims.additionalPermissions.includes(Permissions.ContentManager)
    ) {
      this.menu.push({
        name: 'Content Library',
        icon: 'assets/icons/content.svg',
        nzIcon: 'container',
        subMenu: [
          {
            name: 'Manage Content',
            link: '/content',
          },
        ],
      });
    }

    if (
      customClaims.additionalPermissions.includes(
        Permissions.AdminManagePartner,
      )
    ) {
      this.menu.push({
        name: 'Partners',
        icon: 'assets/icons/corporate.svg',
        nzIcon: 'api',
        subMenu: [
          {
            name: 'Manage Partners',
            link: '/partner',
          },
        ],
      });
    }

    // if (
    //   customClaims.additionalPermissions.includes(Permissions.RoutineManager)
    // ) {
    //   this.menu.push({
    //     name: 'Routines',
    //     icon: 'assets/icons/challenge.svg',
    //     nzIcon: 'signal',
    //     subMenu: [
    //       {
    //         name: 'Manage Routine',
    //         link: '/routine',
    //       },
    //     ],
    //   });
    // }

    // if (customClaims.additionalPermissions.includes(Permissions.BytesManager)) {
    //   this.menu.push({
    //     name: 'Bytes',
    //     icon: 'assets/icons/challenge.svg',
    //     nzIcon: 'pie-chart',
    //     subMenu: [
    //       {
    //         name: 'Manage Bytes',
    //         link: '/bytes',
    //       },
    //     ],
    //   });
    // }

    if (
      customClaims.additionalPermissions.includes(
        Permissions.AddRecipeInContent,
      )
    ) {
      const index = this.menu.findIndex(
        (data) => data.name === 'Content Library',
      );
      this.menu[index].subMenu?.push({
        name: 'Add Recipe',
        link: '/content/recipe/add',
      });
    }
  }

  handleRoles(customClaims: IAnyoUser['customClaims']) {
    if (!customClaims || !customClaims.roles) return;

    if (customClaims.roles.includes(Roles.EmailTemplatesManager)) {
      this.menu.push({
        name: 'Email Templates',
        nzIcon: 'mail',
        subMenu: [
          {
            name: 'Manage Templates for Partners',
            link: '/email-templates/manage',
          },
        ],
      });
    }

    if (customClaims.roles.includes(Roles.ManagePromoCodes)) {
      this.menu.push({
        name: 'Promo Codes',
        icon: 'assets/icons/listener.svg',
        nzIcon: 'barcode',
        subMenu: [
          {
            name: 'Manage Promo Codes',
            link: '/promo-code/manage',
          },
        ],
      });
    }

    if (customClaims.roles.includes(Roles.ViewUserProfile)) {
      this.menu.push({
        name: 'User Profile',
        icon: 'assets/icons/corporate.svg',
        nzIcon: 'profile',
        subMenu: [
          {
            name: 'Search User',
            link: '/user-profile',
          },
        ],
      });
    }
    if (customClaims.roles.includes(Roles.PhysicalFitnessManager)) {
      this.menu.push({
        name: 'Physical Fitness',
        icon: 'assets/icons/corporate.svg',
        nzIcon: 'trophy',
        subMenu: [
          {
            name: 'Challenges',
            link: '/physical-fitness/challenges',
          },
          {
            name: 'Fitness Logs',
            link: '/physical-fitness/logs',
          },
        ],
      });
    }
    const lyfisSubMenu = [
      {
        name: 'Manage Lyfis Partner Profiles',
        link: '/lyfis',
      },
      {
        name: 'Manage Lyfis Questions',
        link: '/lyfis/questions',
      },
      {
        name: 'Scorecard Compiler',
        link: '/lyfis/scorecard-compiler',
      },
      {
        name: 'Scorecard Compilations',
        link: '/lyfis/scorecard-compilations',
      },
      {
        name: 'Lyfis Testing',
        link: '/lyfis/testing',
      },
    ];
    if (
      customClaims.additionalPermissions.includes(
        Permissions.ViewUserLyfisScores,
      )
    ) {
      lyfisSubMenu.push({
        name: 'View Lyfis Scores',
        link: '/lyfis/view-scores',
      });
    }
    this.menu.push({
      name: 'Lyfis',
      icon: 'assets/icons/corporate.svg',
      nzIcon: 'trophy',
      subMenu: lyfisSubMenu,
    });

    // if (customClaims.roles.includes(Roles.TalksManager)) {
    //   this.menu.push({
    //     name: 'Anyo Talks',
    //     icon: 'assets/icons/dashboard.svg',
    //     nzIcon: 'contacts',
    //     subMenu: [
    //       {
    //         name: 'Talks',
    //         link: 'talks/',
    //       },
    //       {
    //         name: 'Talk Sessions',
    //         link: 'talks/talk-logs/grid',
    //       },
    //     ],
    //   });
    // }

    if (customClaims.roles.includes(Roles.CircleManager)) {
      this.menu.push({
        name: 'Circles',
        icon: 'assets/icons/content.svg',
        nzIcon: 'trophy',
        subMenu: [
          {
            name: 'Manage Circles',
            link: '/circles',
          },
          {
            name: 'Manage Moderators',
            link: '/circles/moderators',
          },
        ],
      });
    }

    if (
      customClaims.additionalPermissions.includes(
        Permissions.AddOrUpdateSeasonalTags,
      )
    ) {
      this.menu.push({
        name: 'Seasonal Tags',
        icon: 'assets/icons/dashboard.svg',
        nzIcon: 'contacts',
        subMenu: [
          {
            name: 'Manage Seasonal Tags',
            link: '/circles/manage/seasonaltags',
          },
        ],
      });
    }

    if (
      customClaims.roles.includes(Roles.CirclesModerator) ||
      customClaims.roles.includes(Roles.CircleManager)
    ) {
      this.menu.push({
        name: 'Posts',
        icon: 'assets/icons/challenge.svg',
        nzIcon: 'schedule',
        subMenu: [
          {
            name: 'Manage Posts',
            link: '/posts',
          },
        ],
      });
    }
    if (customClaims.roles.includes(Roles.EventsManager)) {
      this.menu.push({
        name: 'Events',
        icon: 'assets/icons/challenge.svg',
        nzIcon: 'calendar',
        subMenu: [
          {
            name: 'Manage Events',
            link: '/events',
          },
          {
            name: 'Manage Campaigns',
            link: '/campaigns',
          },
          {
            name: 'View Events Rating',
            link: '/events/events-feedback',
          },
          // {
          //   name: 'Event Registrations',
          //   link: '/events/registrations',
          // }
          {
            name: 'Manage Forums',
            link: '/events/forums',
          },
        ],
      });
    }
    if (customClaims.roles.includes(Roles.ManageEventBidding)) {
      const index = this.menu.findIndex((data) => data.name === 'Events');
      this.menu[index].subMenu?.push({
        name: 'Manage Events Bidding',
        link: '/events/events-bidding',
      });
    }

    if (customClaims.roles.includes(Roles.ManageSurveys)) {
      this.menu.push({
        name: 'Anyo Survey',
        icon: 'assets/icons/challenge.svg',
        nzIcon: 'issues-close',
        subMenu: [
          {
            name: 'Manage Survey',
            link: '/survey',
          },
        ],
      });
    }
    if (
      customClaims.additionalPermissions.includes(
        Permissions.AdminShowSurveyResult,
      )
    ) {
      const index = this.menu.findIndex((data) => data.name === 'Anyo Survey');
      this.menu[index].subMenu?.push({
        name: 'View Survey Response',
        link: '/survey/response',
      });
    }
    if (customClaims.roles.includes(Roles.ManageAnyoPackages)) {
      this.menu.push({
        name: 'Packs',
        icon: 'assets/icons/challenge.svg',
        nzIcon: 'paper-clip',
        subMenu: [
          {
            name: 'Manage Anyo packs',
            link: '/packs',
          },
          {
            name: 'Manage Anyo packs subscription',
            link: '/packs/packs-subscription',
          },
          {
            name: 'Chat With Prompt',
            link: '/packs/chat',
          },
        ],
      });
    }
    // if (customClaims.roles.includes(Roles.AdminManageMasterData)) {
    this.menu.push({
      name: 'Master Data Config',
      icon: 'assets/icons/dashboard.svg',
      nzIcon: 'api',
      subMenu: [
        {
          name: 'Manage Master Data Config',
          link: '/master-data-config',
        },
        {
          name: 'Search Master Data',
          link: '/master-data-config/search',
        },
      ],
    });
    // }
    // if(customClaims.roles.includes(Roles.ManageRuleEngine)) {
    this.menu.push({
      name: 'Rule Engine',
      icon: 'assets/icons/challenge.svg',
      nzIcon: 'property-safety',
      subMenu: [
        {
          name: 'Manage Rule Engine',
          link: '/rule-engine',
        },
        {
          name: 'Global Policy Rules',
          link: '/rule-engine/global-policy',
        },
      ],
    });
    // }
    if (customClaims.roles.includes(Roles.ManageGoals)) {
      this.menu.push({
        name: 'Anyo Goals',
        icon: 'assets/icons/challenge.svg',
        nzIcon: 'aim',
        subMenu: [
          {
            name: 'Plan Progressor Monitor',
            link: '/goals',
          },
          {
            name: 'Goals',
            link: '/goals/list',
          },
          {
            name: 'Unassigned Referrals',
            link: '/goals/referrals',
          },
          {
            name: 'Objectives Catalog',
            link: '/goals/objectives',
          },
        ],
      });
    }
    if (customClaims.roles.includes(Roles.ManageReengageAudit)) {
      this.menu.push({
        name: 'Reengagement Audit',
        icon: 'assets/icons/challenge.svg',
        nzIcon: 'audit',
        subMenu: [
          {
            name: 'Audit Log',
            link: '/reengage-audit',
          },
        ],
      });
    }
    if (customClaims.roles.includes(Roles.SuperAdmin)) {
      this.menu.push({
        name: 'Roles And Permission',
        icon: 'assets/icons/challenge.svg',
        nzIcon: 'property-safety',
        subMenu: [
          {
            name: 'Manage Roles And Permission',
            link: '/roles-permission',
          },
        ],
      });
    }
  }
}
